
import { useState, useEffect } from 'react';
import { Profile } from '@/hooks/useProfile';
import { generateBio } from '@/utils/bioGenerator';
import { useToast } from '@/hooks/use-toast';

export const useBasicInfoForm = (
  profile: Profile, 
  setProfile: (field: keyof Profile | string, value: any) => void
) => {
  const [isGeneratingBio, setIsGeneratingBio] = useState(false);
  const { toast } = useToast();
  
  // Map experience number to experience level
  const getExperienceLevelFromYears = (years: number | null): string => {
    if (years === null || years === undefined) return '';
    
    if (years < 1) return 'less_than_1';
    if (years <= 2) return '1_to_2';
    if (years <= 5) return '3_to_5';
    if (years <= 10) return '6_to_10';
    if (years <= 15) return '11_to_15';
    if (years <= 20) return '16_to_20';
    return 'more_than_20';
  };
  
  // Map experience level to years
  const getYearsFromExperienceLevel = (level: string): number => {
    switch (level) {
      case 'less_than_1': return 0;
      case '1_to_2': return 1;
      case '3_to_5': return 3;
      case '6_to_10': return 6;
      case '11_to_15': return 11;
      case '16_to_20': return 16;
      case 'more_than_20': return 21;
      default: return 0;
    }
  };
  
  const [experienceLevel, setExperienceLevel] = useState(
    getExperienceLevelFromYears(profile.years_experience)
  );
  
  // Update years_experience when experience level changes
  useEffect(() => {
    if (experienceLevel) {
      setProfile('years_experience', getYearsFromExperienceLevel(experienceLevel));
    }
  }, [experienceLevel, setProfile]);

  const handleGenerateBio = async () => {
    if (!profile.username || !profile.profession) {
      toast({
        title: "Missing information",
        description: "Please fill in your name and profession to generate a bio.",
        variant: "destructive",
      });
      return;
    }
    
    setIsGeneratingBio(true);
    try {
      const generatedBio = await generateBio({
        name: profile.username,
        profession: profile.profession,
        skills: profile.skills || [],
        experience: profile.years_experience || 0,
        education: profile.education || '',
        languages: profile.languages || [],
        location: profile.location || ''
      });
      
      setProfile('bio', generatedBio);
      toast({
        title: "Bio generated",
        description: "Your bio has been generated successfully.",
      });
    } catch (error) {
      console.error('Error generating bio:', error);
      toast({
        title: "Error generating bio",
        description: "There was a problem generating your bio. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingBio(false);
    }
  };

  return {
    experienceLevel,
    isGeneratingBio,
    setExperienceLevel,
    getYearsFromExperienceLevel,
    handleGenerateBio
  };
};

export default useBasicInfoForm;
